<template>
  <div class="inquiry-report">
    <!-- Control Bar -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">New Admission List</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ totalStudents }} Records
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem;" />
      <p class="mt-3 font-weight-bold">Loading records...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" @click="loadReportData" class="mt-3">Retry</b-button>
    </div>

    <!-- Report Content -->
    <div v-else class="report-page">
      <!-- Modern Header -->
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div class="logo-wrapper mr-3" v-if="schoolLogo">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img">
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName }}<span v-if="headerBranch" class="text-muted"> - {{ headerBranch }}</span>
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">New Admission List</span>
              <span v-if="dateRangeLabel" class="date-range-text mr-2">
                <strong>{{ dateRangeLabel }}</strong>
              </span>
              <span class="date-range-text">
                <strong>Generated on {{ currentDateTime }}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!campusGroups.length" class="empty-state">
        <feather-icon icon="InboxIcon" size="32" class="text-muted mb-2" />
        <p>No records found for the selected criteria</p>
      </div>

      <section
        v-for="(campus, campusIndex) in campusGroups"
        :key="campus.key"
        class="campus-block"
      >
        <div class="campus-heading">
          <div class="campus-heading__left">
            <span class="campus-heading__index">{{ campusIndex + 1 }}</span>
            <span class="campus-heading__name">{{ campus.name }}</span>
          </div>
          <span class="campus-heading__count">{{ campus.studentCount }} records</span>
        </div>

        <div
          v-for="(dept, deptIndex) in campus.departments"
          :key="campus.key + '-dept-' + deptIndex"
          class="department-block"
        >
          <div class="department-heading">
            <span class="department-heading__name">{{ dept.name }}</span>
            <span class="department-heading__count">{{ dept.students.length }} records</span>
          </div>

          <div class="table-container">
            <table class="ledger-style-table">
              <thead>
                <tr>
                  <th class="text-center" style="width: 4%">#</th>
                  <th class="text-center" style="width: 10%">GR NO.</th>
                  <th class="text-left" style="width: 18%">NAME</th>
                  <th class="text-left" style="width: 18%">FATHER'S NAME</th>
                  <th class="text-center" style="width: 14%">DATE OF ADMISSION</th>
                  <th class="text-center" style="width: 12%">CLASS</th>
                  <th class="text-left" style="width: 24%">ADDRESS</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(student, index) in dept.students"
                  :key="campus.key + '-' + deptIndex + '-' + index"
                >
                  <td class="text-center font-weight-bold">{{ index + 1 }}</td>
                  <td class="text-center">{{ student.grNo || "-" }}</td>
                  <td class="text-left font-weight-bold">
                    {{ displayName(student.name) }}
                  </td>
                  <td class="text-left">
                    {{
                      displayName(
                        student.fatherName ||
                          student.father_name ||
                          student.father
                      )
                    }}
                  </td>
                  <td class="text-center">
                    {{
                      formatDate(
                        student.date_of_admission ||
                          student.doa ||
                          student.admissionDate
                      )
                    }}
                  </td>
                  <td class="text-center">
                    {{ student.className || student.class || "-" }}
                  </td>
                  <td class="text-left address-cell">
                    {{ student.address || "-" }}
                  </td>
                </tr>
                <tr v-if="!dept.students.length">
                  <td colspan="7" class="text-center py-4 no-data">
                    No students in this department
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from 'bootstrap-vue'
import axios from 'axios'

export default {
  name: 'NewAdmissionListPrint',
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      campusGroups: [],
      campusName: 'MY SKOOL',
      campusBranch: '',
      schoolLogo: null,
      db: '',
      cID: 0,
      departmentID: 0,
      classID: 0,
      classIDs: '',
      sectionID: 0,
      sectionIDs: '',
      dateFrom: null,
      dateTill: null,
    }
  },
  computed: {
    isAllCampusesView() {
      return Number(this.cID) === 1
    },
    headerBranch() {
      return this.isAllCampusesView ? 'All Campuses' : this.campusBranch || ''
    },
    totalStudents() {
      return this.campusGroups.reduce(
        (sum, campus) => sum + (campus.studentCount || 0),
        0
      )
    },
    dateRangeLabel() {
      if (this.dateFrom && this.dateTill) {
        return `${this.formatDate(this.dateFrom)} to ${this.formatDate(this.dateTill)}`
      }
      if (this.dateFrom) return `From ${this.formatDate(this.dateFrom)}`
      if (this.dateTill) return `Till ${this.formatDate(this.dateTill)}`
      return ''
    },
    currentDateTime() {
      const now = new Date()
      return now.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
  },
  created() {
    this.initReport()
  },
  methods: {
    initReport() {
      const query = this.$route.query
      this.db = query.db || this.$store.state.userData.db
      this.cID = Number(query.cID || query.campusID || 0)
      this.departmentID = Number(query.departmentID || 0)
      this.classID = Number(query.classID || 0)
      this.classIDs = query.classIDs || ''
      this.sectionID = Number(query.sectionID || query.secID || 0)
      this.sectionIDs = query.sectionIDs || query.secIDs || ''
      this.dateFrom = this.normalizeDate(query.dateFrom || query.dtFrom)
      this.dateTill = this.normalizeDate(query.dateTill || query.dtTo)

      if (!this.db) {
        this.error = 'Database identifier is missing.'
        return
      }

      this.loadCampusInfo()
      this.loadReportData()
    },
    async loadCampusInfo() {
      try {
        const branches = this.$store.state.branches || []
        const currentBranch =
          branches.find(b => b.id == this.cID) || this.$store.state.currentBranch
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.name ||
            currentBranch.branch ||
            'MY SKOOL'
          this.campusBranch = this.isAllCampusesView
            ? 'All Campuses'
            : (currentBranch.branch || currentBranch.branchName || '')
          this.schoolLogo = currentBranch.logo
        } else if (this.isAllCampusesView) {
          this.campusBranch = 'All Campuses'
        }
      } catch (e) {
        console.warn('Campus info fetch failed', e)
      }
    },
    async loadReportData() {
      this.loading = true
      this.error = null

      try {
        const params = {
          db: this.db,
          campusID: Number(this.cID) || 0,
          departmentID: Number(this.departmentID) || 0,
          classID: Number(this.classID) || 0,
          sectionID: Number(this.sectionID) || 0,
        }

        if (this.dateFrom) params.dateFrom = this.dateFrom
        if (this.dateTill) params.dateTill = this.dateTill
        if (this.classIDs) params.classIDs = this.classIDs
        if (this.sectionIDs) params.sectionIDs = this.sectionIDs

        const url = `${this.$store.state.domain}Students/NewAdmissionListReport`
        const response = await axios.get(url, {
          params,
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: 'application/json',
          },
        })

        const payload = this.extractPayload(response.data)
        this.campusGroups = this.normalizeCampusGroups(payload)
      } catch (err) {
        console.error('New admission list report error:', err)
        this.error =
          err?.response?.data?.message ||
          'Server error while fetching new admission list.'
        this.campusGroups = []
      } finally {
        this.loading = false
      }
    },
    extractPayload(data) {
      if (!data) return []
      if (Array.isArray(data)) return data
      if (data.status === 'success' && data.data != null) return data.data
      if (Array.isArray(data.data)) return data.data
      if (data.data && typeof data.data === 'object') return data.data
      return data
    },
    normalizeCampusGroups(payload) {
      if (!payload) return []

      if (Array.isArray(payload)) {
        if (!payload.length) return []

        if (this.looksLikeStudent(payload[0])) {
          return this.groupFlatStudents(payload)
        }

        if (
          payload[0].departments ||
          payload[0].campusName ||
          payload[0].campusBranch ||
          payload[0].campusID != null
        ) {
          return payload
            .map((campus, index) => this.normalizeCampus(campus, index))
            .filter(c => c.departments.length)
        }

        if (payload[0].students || payload[0].departmentName) {
          const campus = this.normalizeCampus(
            {
              campusID: this.cID,
              campusName: this.campusBranch || this.campusName,
              campusBranch: this.campusBranch || this.campusName,
              departments: payload,
            },
            0
          )
          return campus.departments.length ? [campus] : []
        }
      }

      if (typeof payload === 'object') {
        if (Array.isArray(payload.campuses)) {
          return this.normalizeCampusGroups(payload.campuses)
        }
        if (Array.isArray(payload.departments)) {
          return this.normalizeCampusGroups([
            {
              campusID: this.cID,
              campusName: this.campusBranch || this.campusName,
              campusBranch: this.campusBranch || this.campusName,
              departments: payload.departments,
            },
          ])
        }
        if (Array.isArray(payload.students)) {
          return this.groupFlatStudents(payload.students)
        }
      }

      return []
    },
    looksLikeStudent(item) {
      if (!item || typeof item !== 'object') return false
      return (
        item.grNo != null ||
        item.name != null ||
        item.date_of_admission != null ||
        item.doa != null
      )
    },
    normalizeCampus(campus, index) {
      const name =
        campus.campusBranch ||
        campus.campusName ||
        campus.branch ||
        campus.name ||
        `Campus ${index + 1}`

      const departments = (campus.departments || campus.Departments || []).map(
        (dept, deptIndex) => this.normalizeDepartment(dept, deptIndex)
      )

      if (!departments.length && Array.isArray(campus.students)) {
        departments.push(
          this.normalizeDepartment(
            {
              departmentName: 'All Departments',
              students: campus.students,
            },
            0
          )
        )
      }

      const studentCount = departments.reduce(
        (sum, dept) => sum + dept.students.length,
        0
      )

      return {
        key: `campus-${campus.campusID ?? index}-${name}`,
        campusID: campus.campusID,
        name,
        departments: departments.filter(d => d.students.length),
        studentCount,
      }
    },
    normalizeDepartment(dept, index) {
      const students = (
        dept.students ||
        dept.Students ||
        dept.items ||
        []
      ).map(s => this.normalizeStudent(s))

      return {
        name:
          dept.departmentName ||
          dept.name ||
          dept.department ||
          `Department ${index + 1}`,
        students,
      }
    },
    normalizeStudent(student) {
      return {
        ...student,
        grNo: student.grNo || student.gr || student.GRNo || '',
        name: student.name || student.studentName || '',
        fatherName:
          student.fatherName ||
          student.father_name ||
          student.father ||
          '',
        date_of_admission:
          student.date_of_admission ||
          student.doa ||
          student.admissionDate ||
          student.dateOfAdmission ||
          null,
        className:
          student.className ||
          student.class ||
          student.clsName ||
          student.meta_class ||
          '',
        address: student.address || student.residentialArea || '',
      }
    },
    groupFlatStudents(students) {
      const campusMap = {}
      const campuses = []

      students.forEach(raw => {
        const student = this.normalizeStudent(raw)
        const campusID = raw.campusID != null ? raw.campusID : student.campusID
        const campusName =
          raw.campusBranch ||
          raw.campusName ||
          raw.branch ||
          this.getCampusNameById(campusID) ||
          this.campusBranch ||
          this.campusName ||
          'Unknown Campus'
        const campusKey =
          campusID != null && campusID !== ''
            ? `id-${campusID}`
            : `name-${campusName}`

        if (!campusMap[campusKey]) {
          campusMap[campusKey] = {
            key: campusKey,
            campusID,
            name: campusName,
            deptMap: {},
            departments: [],
            studentCount: 0,
          }
          campuses.push(campusMap[campusKey])
        }

        const campus = campusMap[campusKey]
        const deptName =
          raw.departmentName ||
          raw.department ||
          student.departmentName ||
          'School Department'
        const deptKey = String(deptName).toLowerCase()

        if (!campus.deptMap[deptKey]) {
          const dept = { name: deptName, students: [] }
          campus.deptMap[deptKey] = dept
          campus.departments.push(dept)
        }

        campus.deptMap[deptKey].students.push(student)
        campus.studentCount += 1
      })

      return campuses
        .map(c => ({
          key: c.key,
          campusID: c.campusID,
          name: c.name,
          departments: c.departments,
          studentCount: c.studentCount,
        }))
        .sort((a, b) =>
          String(a.name).localeCompare(String(b.name), undefined, {
            sensitivity: 'base',
          })
        )
    },
    getCampusNameById(id) {
      if (id == null || id === '') return ''
      const match = (this.$store.state.branches || []).find(b => b.id == id)
      return match ? match.branch || match.name || '' : ''
    },
    normalizeDate(value) {
      if (value === null || value === undefined || value === '') return null
      return value
    },
    displayName(value) {
      const text = (value || '-').toString().trim()
      return text ? text.toUpperCase() : '-'
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      if (Number.isNaN(date.getTime())) {
        return String(dateStr).slice(0, 10)
      }
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    },
    print() {
      window.print()
    },
    close() {
      window.close()
    }
  }
}
</script>

<style lang="scss" scoped>
.inquiry-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: 'Outfit', 'Inter', sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255,255,255,0.3);
  
  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s;
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span { margin-left: 4px; }
  &:active { transform: scale(0.96); }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;
  &:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(108, 92, 231, 0.3); }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;
  &:hover { color: #e74c3c; border-color: #fab1a0; }
}

.report-page {
  background: #ffffff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.12);
  width: 98%;
  max-width: 1200px;
  margin: 30px auto;
}

.report-header-modern {
  border-bottom: 3px solid #333;
  padding-bottom: 20px;
  
  .school-logo-img {
    height: 100px;
    width: auto;
  }
  
  .school-name-full {
    font-size: 1.8rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
  }
  
  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }
  }
}

.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #636e72;
}

.campus-block {
  margin-bottom: 1.75rem;
  page-break-inside: auto;
}

.campus-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1rem;
  margin-bottom: 0;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  page-break-after: avoid;
  page-break-inside: avoid;

  &__left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 0;
  }

  &__index {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 28px;
    border-radius: 6px;
    background: #eef2ff;
    color: #4338ca;
    font-size: 0.8rem;
    font-weight: 800;
    flex-shrink: 0;
  }

  &__name {
    font-size: 1rem;
    font-weight: 800;
    color: #111827;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  &__count {
    font-size: 0.82rem;
    font-weight: 700;
    color: #fff;
    background: #2c3e50;
    padding: 0.3rem 0.75rem;
    border-radius: 999px;
    white-space: nowrap;
  }
}

.department-block {
  border: 1px solid #dee2e6;
  border-top: none;

  &:last-child {
    border-radius: 0 0 8px 8px;
    overflow: hidden;
  }
}

.department-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.7rem 1rem;
  background: #f1f3f5;
  border-bottom: 1px solid #dee2e6;
  page-break-after: avoid;

  &__name {
    font-size: 0.9rem;
    font-weight: 800;
    color: #111827;
    text-transform: uppercase;
    letter-spacing: 0.2px;
  }

  &__count {
    font-size: 0.78rem;
    font-weight: 700;
    color: #fff;
    background: #636e72;
    padding: 0.25rem 0.65rem;
    border-radius: 999px;
    white-space: nowrap;
  }
}

.campus-block .ledger-style-table {
  margin-top: 0;
  border: none;
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  border: 1px solid #dee2e6;

  th, td {
    border: 1px solid #dee2e6;
    padding: 12px 10px;
    font-size: 0.9rem;
  }

  thead {
    background: #2c3e50;
    th {
      color: white;
      font-weight: 800;
      text-transform: uppercase;
      font-size: 0.85rem;
    }
  }

  tbody tr:nth-child(even) { background-color: #f8f9fa; }
  .address-cell { font-size: 0.8rem; color: #666; }
}

.no-data {
  color: #adb5bd;
}

@media print {
  .no-print { display: none !important; }
  .inquiry-report { background: white; padding: 0; }
  .report-page { box-shadow: none; border: none; padding: 0.5cm; width: 100%; max-width: 100%; margin: 0; }
  .campus-heading {
    background: #f8f9fa !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .campus-heading__count,
  .department-heading__count {
    background: #2c3e50 !important;
    color: #fff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .department-heading {
    background: #f1f3f5 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .campus-block + .campus-block {
    page-break-before: auto;
  }
  .ledger-style-table { 
    th, td { padding: 6px 8px; border: 1px solid #000 !important; color: #000; }
    thead th { background: #eee !important; color: #000 !important; -webkit-print-color-adjust: exact; }
  }
}
</style>
